import {
  Amount,
  AmountsContainer,
  Footer,
  CustomAmountWrapper,
  Wrapper,
} from '@/components/GiftVoucherAmountSelector.styles'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import TextField from '@/components/TextField'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { Button, Spacing } from '@big-red-group/storefront-common-ui'
import InputError from '@/components/InputError'
import { InputWrapper } from '@/components/Input.styles'
import { formatPrice } from '@big-red-group/storefront-common-checkout'
import Select from './Select'

const GiftVoucherAmountSelector: React.FC<{
  amounts: number[]
  allowCustomAmount: boolean
  maxAllowedAmount: number
  selectedAmount?: number
  setSelectedAmount: (amount: number) => void
  onConfirmAmount: () => void
  selectedQty: number
  onQtyChange: (e: string) => void
  setIsCustomAmountAdded: Dispatch<SetStateAction<boolean>>
}> = ({
  amounts,
  allowCustomAmount,
  maxAllowedAmount,
  selectedAmount,
  setSelectedAmount,
  onConfirmAmount,
  selectedQty,
  onQtyChange,
  setIsCustomAmountAdded,
}) => {
  const [useCustomAmount, setUseCustomAmount] = useState<boolean>(false)
  const MAX_QTY = 20
  const qtyOptions = [...Array(MAX_QTY).keys()].map((value) => ({ label: (value + 1).toString(), value: value + 1 }))

  const validationSchema = Yup.object().shape({
    customAmount: Yup.string().matches(/^[1-9][0-9]*$/, 'Invalid amount'),
  })

  const formik = useFormik({
    initialValues: {
      customAmount: '',
    },
    validationSchema,
    onSubmit: async () => {},
  })

  const isAmountSelected = (amount: number): boolean => {
    return !useCustomAmount && selectedAmount === amount
  }

  const selectPredefinedAmount = (amount: number) => {
    setUseCustomAmount(false)
    setSelectedAmount(amount)
  }

  const isConfirmAmountDisabled: boolean = useMemo(() => {
    if (!useCustomAmount) {
      return !selectedAmount
    }
    const customAmountValue = parseInt(formik.values.customAmount)
    if (formik.errors.customAmount || !customAmountValue) {
      return true
    }
    if (customAmountValue > maxAllowedAmount) {
      formik.setErrors({ customAmount: 'Max amount is ' + maxAllowedAmount })
      return true
    }
    return false
  }, [formik.values, useCustomAmount])

  const confirmAmount = () => {
    if (useCustomAmount) {
      // Multiply by 100 to convert to cents
      setSelectedAmount(parseInt(formik.values.customAmount) * 100)
    }
    onConfirmAmount()
  }

  useEffect(() => {
    if (!useCustomAmount) {
      return
    }
    const customAmount = Number(formik.values.customAmount)
    if (!customAmount || customAmount < 1) {
      setSelectedAmount(0)
      return
    }
    setSelectedAmount(customAmount * 100)
  }, [formik.values.customAmount, useCustomAmount])

  const handleCustomAmount = () => {
    setUseCustomAmount(true)
    setIsCustomAmountAdded(true)
  }

  return (
    <>
      <Wrapper data-testid="voucher-amount">
        <AmountsContainer>
          {amounts.map((amount) => (
            <Amount
              key={amount}
              selected={isAmountSelected(amount)}
              onClick={() => selectPredefinedAmount(amount)}
              data-testid={`voucher-amount-${amount}`}
            >
              <span>{formatPrice(amount)}</span>
            </Amount>
          ))}
          {allowCustomAmount && (
            <Amount selected={useCustomAmount} onClick={() => handleCustomAmount()} data-testid="voucher-amount-custom">
              <span>Custom</span>
            </Amount>
          )}
        </AmountsContainer>
        {useCustomAmount && (
          <>
            <Spacing />
            <CustomAmountWrapper>
              <InputWrapper>
                <label className="required">Custom amount</label>
                <TextField
                  name="customAmount"
                  value={formik.values.customAmount}
                  onChange={formik.handleChange}
                  error={!!(formik.touched.customAmount && formik.errors.customAmount)}
                />
                {formik.touched.customAmount && formik.errors.customAmount && (
                  <InputError dataTestId="amount-error-message">{formik.errors.customAmount}</InputError>
                )}
              </InputWrapper>
            </CustomAmountWrapper>
          </>
        )}
      </Wrapper>
      <Spacing size="S" />
      <Footer>
        <InputWrapper>
          <label>Quantity</label>
          <Select name="internationalCode" options={qtyOptions} value={selectedQty} onChange={(e) => onQtyChange(e)} />
        </InputWrapper>
        <Button size="M" onClick={confirmAmount} dataTestId="voucher-amount-confirm" disabled={isConfirmAmountDisabled}>
          Confirm
        </Button>
      </Footer>
    </>
  )
}

export default GiftVoucherAmountSelector
